import * as cld3 from 'cld3-asm';
import { loadBrowserModule } from './cld3BrowserLoader';
import * as francModule from 'franc-min';
import * as iso6393Module from 'iso-639-3';

let cld3InstancePromise = null;

const franc =
  typeof francModule === 'function'
    ? francModule
    : typeof francModule?.franc === 'function'
    ? francModule.franc
    : typeof francModule?.default === 'function'
    ? francModule.default
    : null;

const francAll =
  typeof francModule?.all === 'function'
    ? francModule.all
    : typeof francModule?.francAll === 'function'
    ? francModule.francAll
    : typeof francModule?.default?.all === 'function'
    ? francModule.default.all
    : null;

const iso6393Entries = Array.isArray(iso6393Module)
  ? iso6393Module
  : Array.isArray(iso6393Module.default)
  ? iso6393Module.default
  : Array.isArray(iso6393Module.iso6393)
  ? iso6393Module.iso6393
  : [];

if (!iso6393Entries.length) {
  console.warn('[languageDetector] iso-639-3 entries not found');
}

const iso3ToIso1Map = iso6393Entries.reduce((map, entry) => {
  if (entry.iso6391) {
    map.set(entry.iso6393, entry.iso6391);
  }
  return map;
}, new Map());

const ISO3_TO_ISO1_FALLBACK = {
  afr: 'af',
  ara: 'ar',
  aze: 'az',
  cat: 'ca',
  ces: 'cs',
  dan: 'da',
  deu: 'de',
  ell: 'el',
  eng: 'en',
  est: 'et',
  fin: 'fi',
  fra: 'fr',
  gle: 'ga',
  heb: 'he',
  hin: 'hi',
  hrv: 'hr',
  hun: 'hu',
  ind: 'id',
  ita: 'it',
  jpn: 'ja',
  kor: 'ko',
  lit: 'lt',
  lav: 'lv',
  nld: 'nl',
  nor: 'no',
  pol: 'pl',
  por: 'pt',
  ron: 'ro',
  rus: 'ru',
  slk: 'sk',
  slv: 'sl',
  spa: 'es',
  srp: 'sr',
  swe: 'sv',
  tur: 'tr',
  ukr: 'uk',
  vie: 'vi',
  zho: 'zh',
};

const languageDisplayNames = new Intl.DisplayNames(['en'], {
  type: 'language',
});

export const getLanguageLabel = code => {
  if (!code) return '';

  try {
    return languageDisplayNames.of(code);
  } catch (error) {
    return code.toUpperCase();
  }
};

const resolveDetector = async value => {
  if (!value) {
    return null;
  }

  if (typeof value.then === 'function') {
    return resolveDetector(await value);
  }

  const candidate = value.default && value !== value.default ? value.default : value;

  if (typeof candidate === 'function' && !candidate.LanguageIdentifier) {
    return resolveDetector(await candidate());
  }

  if (candidate?.findLanguage && typeof candidate.findLanguage === 'function') {
    return candidate;
  }

  if (candidate?.detector && typeof candidate.detector === 'function') {
    return resolveDetector(candidate.detector({ maxSentenceLength: 0, maxTextBytes: 700 }));
  }

  if (candidate?.create && typeof candidate.create === 'function') {
    const instance = await candidate.create(0, 700);
    return resolveDetector(instance);
  }

  if (candidate?.initialize && typeof candidate.initialize === 'function') {
    const initialized = await candidate.initialize();
    return resolveDetector(initialized);
  }

  if (candidate?.Module?.LanguageIdentifier) {
    const LanguageIdentifierCtor = candidate.Module.LanguageIdentifier;
    if (LanguageIdentifierCtor?.create) {
      const instance = await LanguageIdentifierCtor.create(0, 700);
      return resolveDetector(instance);
    }

    if (typeof LanguageIdentifierCtor === 'function') {
      return resolveDetector(new LanguageIdentifierCtor(0, 700));
    }
  }

  const LanguageIdentifier =
    candidate?.LanguageIdentifier || candidate?.default?.LanguageIdentifier;

  if (LanguageIdentifier?.create) {
    const instance = await LanguageIdentifier.create(0, 700);
    return resolveDetector(instance);
  }

  if (typeof LanguageIdentifier === 'function') {
    return resolveDetector(new LanguageIdentifier(0, 700));
  }

  if (LanguageIdentifier?.new) {
    return resolveDetector(LanguageIdentifier.new(0, 700));
  }

  return null;
};

const ensureDetector = detector => {
  if (!detector) {
    return null;
  }

  if (detector?.findLanguage && typeof detector.findLanguage === 'function') {
    return detector;
  }

  if (typeof detector === 'function') {
    return {
      findLanguage: detector,
    };
  }

  return null;
};

const logOnce = (() => {
  let didLog = false;
  return message => {
    if (didLog) {
      return;
    }
    didLog = true;
    console.warn(message);
  };
})();

const resolveDetectorFromFactory = async candidate =>
  ensureDetector(
    (await resolveDetector(candidate)) ||
      (await resolveDetector(cld3?.Module)) ||
      (await resolveDetector(cld3?.default?.Module)) ||
      (await resolveDetector(cld3?.default)) ||
      (await resolveDetector(cld3))
  );

const loadDetector = () => {
  if (!cld3InstancePromise) {
    cld3InstancePromise = Promise.resolve()
      .then(async () => {
        try {
          const browserFactory = await loadBrowserModule();
          const detector = await resolveDetectorFromFactory(browserFactory);
          if (detector) {
            console.info('[languageDetector] CLD3 loaded successfully via browser runtime');
            return detector;
          }
        } catch (error) {
          console.warn('[languageDetector] browser runtime failed', error);
        }

        const loadModule = cld3?.loadModule || cld3?.default?.loadModule;
        if (typeof loadModule === 'function') {
          try {
            const module = await loadModule();
            const detector = await resolveDetectorFromFactory(module?.Module || module);
            if (detector) {
              console.info('[languageDetector] CLD3 loaded successfully via loadModule');
              return detector;
            }
          } catch (error) {
            console.warn('[languageDetector] loadModule failed', error);
          }
        }

        const detector = await resolveDetectorFromFactory(null);

        if (detector) {
          console.info('[languageDetector] CLD3 loaded successfully via direct Module');
          return detector;
        }

        console.warn('[languageDetector] CLD3 initialization failed, falling back to franc');
        return null;
      })
      .catch(error => {
        console.warn('[languageDetector] Failed to initialize cld3-asm', error);
        return null;
      });
  }

  return cld3InstancePromise;
};

const formatCld3Result = result => {
  if (!result || !result.language || result.probability <= 0) {
    return null;
  }

  return {
    language: result.language,
    probability: result.probability,
    reliable: result.reliable ?? result.isReliable ?? true,
    proportion: result.proportion,
    label: getLanguageLabel(result.language),
  };
};

const detectWithFranc = text => {
  if (!franc) {
    console.warn('[languageDetector] franc-min not available for fallback detection');
    return null;
  }

  let iso3 = franc(text, { minLength: 2 });

  if (!iso3 || iso3 === 'und') {
    if (typeof francAll === 'function') {
      const ranked = francAll(text, { minLength: 2, only: [] }).find(
        entry => Array.isArray(entry) && entry[0] && entry[0] !== 'und'
      );
      iso3 = Array.isArray(ranked) ? ranked[0] : null;
    }
  }

  if (!iso3 || iso3 === 'und') {
    return null;
  }

  const iso1 = iso3ToIso1Map.get(iso3) || ISO3_TO_ISO1_FALLBACK[iso3] || iso3;
  const normalized = String(iso1).toLowerCase();
  const label = getLanguageLabel(normalized);

  return {
    language: normalized,
    probability: 1,
    reliable: false,
    proportion: null,
    label,
  };
};

const MIN_CLD3_PROBABILITY = 0.6;

const detectWithCld3 = async text => {
  try {
    const detector = await loadDetector();
    if (!detector) {
      return null;
    }
    const result = detector.findLanguage(text);
    const formatted = formatCld3Result(result);

    if (formatted && typeof formatted.probability === 'number') {
      if (formatted.probability < MIN_CLD3_PROBABILITY) {
        return null;
      }
    }

    return formatted;
  } catch (error) {
    console.debug('[languageDetector] cld3 detection failed, falling back to franc-min', error);
    return null;
  }
};

export const detectLanguage = async text => {
  if (!text) {
    return null;
  }

  const normalized = text.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return null;
  }

  const cld3Result = await detectWithCld3(normalized);
  if (cld3Result) {
    return cld3Result;
  }

  return detectWithFranc(normalized);
};
